import React, { useRef, useState } from 'react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Container,
    Divider,
    IconButton,
    InputAdornment,
    Link,
    TextField,
    Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { signInUser, getLocalStorageAuthHeaders } from '../../../app/features/sessions/sessionSlice';
import { getUserProjects } from '../../../app/features/sessions/projectsManagement/sessionManageProjects';
import { AppDispatch, RootState } from '../../../app/store';
import { store } from '../../../app/store';
import * as REQUEST_REQUIREMENTS from '../../../app/api/requestRequirements';
import jinnerLogo from '../../../app/assets/logo-white-name-white-without-background-portrait.png';

const BG = '#15151a';
const CARD_BG = '#1c1c24';
const BRAND_RED = '#c0392b';
const TEXT = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.6)';
const BORDER = 'rgba(255,255,255,0.12)';

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        color: TEXT,
        '& fieldset': { borderColor: BORDER },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px ${CARD_BG} inset`,
            WebkitTextFillColor: TEXT,
            caretColor: TEXT,
        },
    },
    '& .MuiInputLabel-root': { color: TEXT_SECONDARY },
    '& .MuiInputLabel-root.Mui-focused': { color: '#f4f4f6', fontWeight: 700 },
    '& .MuiInputAdornment-root .MuiIconButton-root': { color: TEXT_SECONDARY },
};

function SignIn() {
    const emailRef = useRef<HTMLInputElement>();
    const passwordRef = useRef<HTMLInputElement>();
    const [errors, setErrors] = useState<string[]>([]);
    const [showPassword, setShowPassword] = useState(false);
    const loading = useSelector((state: RootState) => state.session.loading);
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const fromLocation = (location.state as { from?: { pathname: string } } | null)?.from;

    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setErrors([]);
        if (!emailRef.current?.value || !passwordRef.current?.value) {
            return setErrors(['Please fill out all fields']);
        }
        const response = await dispatch(signInUser({
            email: emailRef.current.value,
            password: passwordRef.current.value,
        }));
        if (response.meta?.requestStatus === 'rejected') {
            const p = response.payload as any;
            const msgs = p?.errors ?? (p?.error ? [p.error] : ['An unexpected error occurred. Please try again.']);
            return setErrors(Array.isArray(msgs) ? msgs : [String(msgs)]);
        }
        const nickname = (response.payload as any).data.nickname;
        const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({
            ROUTE_PARAMS: { userNickname: nickname },
        });

        // If user was redirected from a specific protected route (not just the generic
        // contents/home page), go back there. The generic home route is superseded by the
        // "last edited project" redirect below.
        const isGenericHomeRoute = fromLocation?.pathname && /^\/users\/[^/]+\/contents\/?$/.test(fromLocation.pathname);
        if (fromLocation?.pathname && !isGenericHomeRoute) {
            navigate(fromLocation.pathname);
            return;
        }

        // Otherwise navigate to last edited project
        try {
            await dispatch(getUserProjects({
                authHeaders: getLocalStorageAuthHeaders(),
                urlParams: { userNickname: nickname, page: 1 },
            }));
            const allProjects = store.getState().sessionManageProjects.allProjects;
            if (allProjects.length > 0) {
                const last = [...allProjects].sort((a, b) =>
                    new Date(b.project?.updatedAt || 0).getTime() -
                    new Date(a.project?.updatedAt || 0).getTime()
                )[0];
                const ownerNickname = last.project.user?.nickname || nickname;
                const projectRoute = REQUEST_REQUIREMENTS.handlePrivateRoutes({
                    ROUTE_PARAMS: { userNickname: ownerNickname, projectId: last.project.id },
                })?.PROJECT_CONTENTS;
                if (projectRoute) { navigate(projectRoute); return; }
            }
        } catch (_) { /* fallback to HOME */ }

        navigate(PRIVATE_ROUTES.HOME);
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: BG, display: 'flex', alignItems: 'center' }}>
            <Container maxWidth="sm">
                <Card sx={{
                    bgcolor: CARD_BG,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                }}>
                    <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Box component="img" src={jinnerLogo} alt="Jinner" sx={{ height: 80 }} />
                        </Box>

                        <Typography variant="h5" fontWeight={700} color={TEXT} gutterBottom>
                            Sign In
                        </Typography>

                        {errors.length > 0 && (
                            <Alert severity="error" sx={{ mb: 2 }} aria-live="assertive">
                                {errors.map((e, i) => <div key={i}>{e}</div>)}
                            </Alert>
                        )}

                        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
                            <TextField
                                label="Email Address"
                                type="email"
                                inputRef={emailRef}
                                fullWidth
                                required
                                variant="outlined"
                                margin="normal"
                                sx={fieldSx}
                            />
                            <TextField
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                inputRef={passwordRef}
                                fullWidth
                                required
                                variant="outlined"
                                margin="normal"
                                sx={fieldSx}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(s => !s)}
                                                edge="end"
                                            >
                                                {showPassword ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Box sx={{ textAlign: 'right', mt: 0.5 }}>
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={() => navigate(REQUEST_REQUIREMENTS.RESET_PASSWORD_ENDPOINT)}
                                    sx={{ color: TEXT_SECONDARY, fontSize: '0.8rem', textDecoration: 'none', '&:hover': { color: TEXT } }}
                                >
                                    Forgot password?
                                </Link>
                            </Box>

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={loading}
                                sx={{
                                    mt: 3,
                                    py: 1.4,
                                    bgcolor: BRAND_RED,
                                    '&:hover': { bgcolor: '#a93226' },
                                    '&.Mui-disabled': { bgcolor: 'rgba(192,57,43,0.4)' },
                                }}
                            >
                                {loading ? <CircularProgress size={22} color="inherit" /> : 'Sign In'}
                            </Button>
                        </Box>
                    </CardContent>

                    <Divider sx={{ borderColor: BORDER }} />

                    <CardActions sx={{ justifyContent: 'center', py: 2 }}>
                        <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                            Don&apos;t have an account?{' '}
                            <Link
                                component="button"
                                onClick={() => navigate('/auth')}
                                sx={{ color: BRAND_RED, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                                Create one
                            </Link>
                        </Typography>
                    </CardActions>
                </Card>
            </Container>
        </Box>
    );
}

export default SignIn;
